import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { EJobType } from '../job-type.enum';
import { IFilterColumn } from '../IFilterColumn';
import { IJob } from '../IJob';
import { DateUtil } from 'app/core/utils/DateUtil';

@Component({
  selector: 'job-schedules',
  templateUrl: 'job-schedules.component.html',
  styleUrls: ['job-schedules.component.scss']
})
export class JobSchedulesComponent {
  @Input() applyDisabled = false;
  @Input() currentJob: IJob;
  @Input() jobType: EJobType;
  @Input() isDevelopment: boolean;
  @Input() areOtherTabsValid: boolean = false;
  @Input() jobStates: { [key: string]: { rescheduleJob: boolean, switchActivated: boolean, originalNextRuntimeDate: Date, nextRuntimeDate: Date } } = {};
  @Output() validationChange = new EventEmitter<boolean>();
  @Output() saveJob = new EventEmitter<IJob>();
  @Output() cancel = new EventEmitter();

  public columns: Array<IFilterColumn>;
  public isValidScheduleName: { [stepId: string]: boolean } = {};
  public scheduleTypes = [
    { name: 'Hourly', value: 'Hours' },
    { name: 'Minutely', value: 'Minutes' },
    { name: 'Monthly', value: 'Monthly' },
    { name: 'Weekly', value: 'Weekly' },
    { name: 'Daily', value: 'Daily' }
  ];
  public dateMin: Date = new Date();
  public dateMax: Date = new Date();
  public validationMessage: string = '';
  public showValidationMessage: boolean = false;

  private _defaultcolumns: Array<IFilterColumn> = [
    { key: 'schName', value: 'JOBS.COLUMN_NAME', visible: true, type: 'text' },
    { key: 'schRecurring', value: 'JOBS.COLUMN_RECURRING', readonly: true, visible: true, type: 'boolean' },
    { key: 'schOccurs', value: 'JOBS.COLUMN_OCCURS', visible: true, type: 'text' },
    { key: 'idSchType', value: 'JOBS.COLUMN_TYPE', visible: true, type: 'type' },
    { key: 'schStartDate', value: 'JOBS.COLUMN_START_DATE', visible: true, type: 'date' },
    { key: 'nextRuntime', value: 'JOBS.COLUMN_NEXT_RUN_TIME', visible: true, type: 'date' }
  ];
  private _userJobColumns: Array<IFilterColumn> = [
    { key: 'name', value: 'JOBS.COLUMN_NAME', visible: true, type: 'text' },
    { key: 'recurring', value: 'JOBS.COLUMN_RECURRING', readonly: true, visible: true, type: 'boolean' },
    { key: 'recurrence', value: 'JOBS.COLUMN_OCCURS', visible: true, type: 'frequency' },
    { key: 'type', value: 'JOBS.COLUMN_TYPE', visible: true, type: 'frequencyType' },
    { key: 'startDate', value: 'JOBS.COLUMN_START_DATE', visible: true, type: 'date' }
  ];

  constructor() {
    this.dateMax.setFullYear(this.dateMax.getFullYear() + 10);
  }

  ngOnInit(): void {
    if (this.currentJob?.schedules) {
      this.sortSchedulesAlphabetically();
      this.initScheduleNameValidation();
    } else {
      this.validationChange.emit(true);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    switch (this.jobType) {
      case EJobType.User:
        this.columns = this._userJobColumns;
        break;
      default:
        this.columns = this._defaultcolumns;
        break;
    }

    if (changes.currentJob) {
      if (this.currentJob?.schedules) {
        this.sortSchedulesAlphabetically();
        this.initScheduleNameValidation();
      } else {
        this.validationChange.emit(true);
      }
    }
    
    if (changes.areOtherTabsValid) {
      this.updateValidationMessage();
    }
  }

  getOrCreateScheduleId(schedule: any): string {
    if (!schedule.uuid) schedule.uuid = uuidv4();
    return schedule.uuid;
  }

  numberOnly(event: KeyboardEvent): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  public validateInputScheduleName(event: Event, schedule: any): void {
    const inputElement = event.target as HTMLInputElement;
    schedule.name = inputElement.value;
    this.initScheduleNameValidation();
  }

  public onDateChange(newValue: any, schedule: any, col: any) {
    const dateString = DateUtil.converDateToStringNoAddOffset(newValue);
    const dateToSave = DateUtil.addOffset(dateString, false, false);
    schedule['startDate'] = dateToSave;
    schedule['startHour'] = dateToSave.replace('Z', '').split('T')[1];
  }

  public applyChanges() {
    // Sincronizar con el estado compartido de settings
    if (this.jobStates[this.currentJob.guid]) {
      DateUtil.setTimeToDate(this.jobStates[this.currentJob.guid].nextRuntimeDate, DateUtil.getTimeFromDate(this.jobStates[this.currentJob.guid].nextRuntimeDate));
      this.currentJob.nextRuntime = DateUtil.returnDateToSave(this.jobStates[this.currentJob.guid].nextRuntimeDate);
      
      // Actualizar schedules
      if (this.currentJob.schedules) {
        this.currentJob.schedules.forEach(schedule => {
          schedule.nextRunTime = this.currentJob.nextRuntime;
        });
      }
    }
    
    this.saveJob.emit(this.currentJob);
  }

  public cancelChanges() {
    this.cancel.emit();
  }

  private validateScheduleName(scheduleName: string): boolean {
    const isValidFormat = /^[a-zA-Z0-9]*$/.test(scheduleName);
    const isValidLength = scheduleName.length >= 1 && scheduleName.length <= 26;

    return isValidFormat && isValidLength;
  }

  private initScheduleNameValidation(): void {
    this.isValidScheduleName = {};
    if (this.currentJob?.schedules) {
      for (const schedule of this.currentJob.schedules) {
        this.isValidScheduleName[this.getOrCreateScheduleId(schedule)] = this.validateScheduleName(schedule.name);
      }
    }
    this.checkDuplicateScheduleNames();
    this.validationChange.emit(this.allScheduleNamesValid);
    this.updateValidationMessage();
  }

  private checkDuplicateScheduleNames(): void {
    if (!this.currentJob?.schedules) return;

    const nameCount: { [name: string]: number } = {};
    for (const schedule of this.currentJob.schedules) {
      const name = schedule.name || '';
      nameCount[name] = (nameCount[name] || 0) + 1;
    }

    for (const schedule of this.currentJob.schedules) {
      const id = this.getOrCreateScheduleId(schedule);
      const name = schedule.name || '';
      if (name && nameCount[name] > 1) {
        this.isValidScheduleName[id] = false;
      }
    }
  }

  private sortSchedulesAlphabetically(): void {
    if (this.currentJob?.schedules) {
      this.currentJob.schedules.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }
  }

  private updateValidationMessage(): void {
    if (this.isDevelopment) {
      this.resetValidationMessage();
      return;
    }

    const hasInvalidScheduleNames = !this.allScheduleNamesValid;
    const hasOtherValidationIssues = !this.areOtherTabsValid;

    this.showValidationMessage = hasInvalidScheduleNames || hasOtherValidationIssues;
    this.validationMessage = this.getValidationMessageKey(hasInvalidScheduleNames, hasOtherValidationIssues);
  }

  private resetValidationMessage(): void {
    this.showValidationMessage = false;
    this.validationMessage = '';
  }

  private getValidationMessageKey(hasInvalidScheduleNames: boolean, hasOtherValidationIssues: boolean): string {
    if (!hasInvalidScheduleNames && !hasOtherValidationIssues) {
      return '';
    }
    
    if (hasInvalidScheduleNames && hasOtherValidationIssues) {
      return 'JOBS.STEPS_SCHEDULES_NAME_AND_OTHER_TABS_VALIDATION_MESSAGE';
    }
    
    return hasInvalidScheduleNames 
      ? 'JOBS.STEPS_SCHEDULES_NAME_VALIDATION_MESSAGE'
      : 'JOBS.USERJOBS_VALIDATION_ERRORS_IN_OTHER_TABS';
  }

  get allScheduleNamesValid(): boolean {
    const values = Object.values(this.isValidScheduleName);
    if (!values.length) return true;
    return values.every(v => v === true);
  }
}
